import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, Loader2, LogOut, LockOpen, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ROLES = [
  { value: "administrator", label: "Administrator" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

const ACCESS_FIELDS = [
  ["finance_access", "Finance"],
  ["investor_access", "Investors"],
  ["decisions_access", "Decisions & Risks"],
];

const roleBadge = (role) =>
  role === "administrator"
    ? "bg-[#E0A82E]/15 text-[#A16207] border-[#E0A82E]/40"
    : role === "editor"
      ? "bg-[#1D4ED8]/10 text-[#1D4ED8] border-[#1D4ED8]/25"
      : "bg-[#EEF2FB] text-[#5A6B8C] border-[#DCE3F2]";

export default function AdminUsers() {
  const { currentUser, logout } = useUser();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [signoutFor, setSignoutFor] = useState(null);

  const logoutSelf = useCallback(async () => {
    await logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await api.listUsers());
    } catch (err) {
      console.error("Failed to load users:", err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleAccess = async (u, field) => {
    try {
      const updated = await api.updateUser(u.id, { [field]: !u[field] });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success("Permissions updated");
    } catch (err) {
      console.error("Failed to update permissions:", err);
    }
  };

  const toggleActive = async (u) => {
    try {
      const updated = await api.updateUser(u.id, { active: !u.active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success(updated.active ? "Account activated" : "Account deactivated");
    } catch (err) {
      console.error("Failed to toggle account status:", err);
    }
  };

  const changeRole = async (u, role) => {
    try {
      const updated = await api.updateUser(u.id, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success("Role updated");
    } catch (err) {
      console.error("Failed to update role:", err);
    }
  };

  const unlock = async (u) => {
    try {
      await api.unlockUser(u.id);
      toast.success(`${u.name}'s account unlocked`);
      load();
    } catch (err) {
      console.error("Failed to unlock account:", err);
      toast.error("Failed to unlock account");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="User Management"
        description="Manage who can access the War Room, their role and the areas they can see. Password resets are handled here by administrators."
        actions={
          <Button data-testid="add-user-button" onClick={() => setCreateOpen(true)} className="gap-2 bg-[#1D4ED8] hover:bg-[#1E40AF]">
            <Plus size={16} /> Add User
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#1D4ED8]" />
        </div>
      ) : (
        <div data-testid="users-table" className="overflow-hidden rounded-xl border border-[#E4E9F3] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-[#E4E9F3] bg-[#F5F8FF] text-left text-xs uppercase tracking-wider text-[#5A6B8C]">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  {ACCESS_FIELDS.map(([, l]) => (
                    <th key={l} className="px-4 py-3 text-center font-medium">{l}</th>
                  ))}
                  <th className="px-4 py-3 text-center font-medium">Active</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === currentUser.id;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#EEF2FB] last:border-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#14245F]">{u.name}{isSelf && <span className="ml-1.5 text-xs font-normal text-[#5A6B8C]">(you)</span>}</p>
                          <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${roleBadge(u.role)}`}>{u.role}</Badge>
                        </div>
                        <p className="text-xs text-[#5A6B8C]">{u.email}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {u.must_reset && <span data-testid={`reset-pending-${u.email}`} className="inline-flex items-center gap-1 rounded-full border border-[#E0A82E]/40 bg-[#E0A82E]/12 px-2 py-0.5 text-[10px] font-semibold text-[#A16207]"><KeyRound size={10} /> Reset pending</span>}
                          {!u.active && <span className="inline-flex items-center gap-1 rounded-full border border-[#DC2626]/30 bg-[#DC2626]/10 px-2 py-0.5 text-[10px] font-semibold text-[#B91C1C]">Inactive</span>}
                          {u.lockout?.locked ? (
                            <span data-testid={`lockout-status-${u.email}`} className="inline-flex items-center gap-1 rounded-full border border-[#DC2626]/30 bg-[#DC2626]/10 px-2 py-0.5 text-[10px] font-semibold text-[#B91C1C]">
                              <Lock size={10} /> Locked · unlocks {new Date(u.lockout.locked_until).toLocaleTimeString()}
                            </span>
                          ) : u.lockout?.failed_attempts > 0 ? (
                            <span data-testid={`lockout-status-${u.email}`} className="inline-flex items-center gap-1 rounded-full border border-[#E0A82E]/40 bg-[#E0A82E]/12 px-2 py-0.5 text-[10px] font-semibold text-[#A16207]">
                              {u.lockout.failed_attempts} failed attempt{u.lockout.failed_attempts !== 1 ? "s" : ""}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={isSelf}>
                          <SelectTrigger data-testid={`role-select-${u.email}`} className="h-8 w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r.value} value={r.value}>
                                <Badge variant="outline" className={roleBadge(r.value)}>{r.label}</Badge>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      {ACCESS_FIELDS.map(([field]) => (
                        <td key={field} className="px-4 py-3 text-center">
                          <Switch
                            data-testid={`access-${field}-${u.email}`}
                            checked={!!u[field]}
                            onCheckedChange={() => toggleAccess(u, field)}
                            disabled={u.role === "administrator"}
                          />
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center">
                        <Switch
                          data-testid={`active-toggle-${u.email}`}
                          checked={!!u.active}
                          onCheckedChange={() => toggleActive(u)}
                          disabled={isSelf}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {(u.lockout?.locked || u.lockout?.failed_attempts > 0) && (
                            <Button
                              data-testid={`unlock-user-${u.email}`}
                              size="sm"
                              variant="ghost"
                              className="h-8 gap-1.5 text-[#16A34A] hover:text-[#15803D]"
                              onClick={() => unlock(u)}
                              title="Unlock account and clear failed attempts"
                            >
                              <LockOpen size={15} /> Unlock
                            </Button>
                          )}
                          <Button
                            data-testid={`reset-password-${u.email}`}
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1.5 text-[#5A6B8C]"
                            onClick={() => setResetFor(u)}
                          >
                            <KeyRound size={15} /> Reset
                          </Button>
                          <Button
                            data-testid={`signout-sessions-${u.email}`}
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1.5 text-[#5A6B8C]"
                            onClick={() => setSignoutFor(u)}
                            title="Sign out of all sessions"
                          >
                            <LogOut size={15} />
                          </Button>
                          {!isSelf && (
                            <Button
                              data-testid={`delete-user-${u.email}`}
                              size="sm"
                              variant="ghost"
                              className="h-8 gap-1.5 text-red-600 hover:text-red-700"
                              onClick={() => setDeleteFor(u)}
                            >
                              <Trash2 size={15} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} />
      <DeleteUserDialog user={deleteFor} onClose={() => setDeleteFor(null)} onDeleted={load} />
      <SignoutDialog user={signoutFor} isSelf={signoutFor?.id === currentUser.id} onClose={() => setSignoutFor(null)} onDone={logoutSelf} />
    </>
  );
}

function CreateUserDialog({ open, onOpenChange, onCreated }) {
  const empty = { name: "", email: "", password: "", role: "viewer" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.email || form.password.length < 8) {
      toast.error("Name, email and a password of at least 8 characters are required");
      return;
    }
    setBusy(true);
    try {
      await api.createUser(form);
      toast.success("User created");
      onOpenChange(false);
      setForm(empty);
      onCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "string") toast.error(d);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
          <DialogDescription>Create a new account. Access defaults apply based on the role and can be adjusted afterwards.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input data-testid="new-user-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input data-testid="new-user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@edjucat.com" />
          </div>
          <div className="space-y-1.5">
            <Label>Temporary Password</Label>
            <Input data-testid="new-user-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 8 characters" />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="submit-new-user" onClick={submit} disabled={busy} className="bg-[#1D4ED8] hover:bg-[#1E40AF]">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Create User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setPassword("");
  }, [user]);

  const submit = async () => {
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.resetUserPassword(user.id, password);
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "string") toast.error(d);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
          <DialogDescription>Set a new temporary password for {user?.name}. Share it securely with them.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>New Password</Label>
          <Input data-testid="reset-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="submit-reset-password" onClick={submit} disabled={busy} className="bg-[#1D4ED8] hover:bg-[#1E40AF]">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Reset Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ user, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.deleteUser(user.id);
      toast.success(`${user.name} removed`);
      onClose();
      onDeleted();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "string") toast.error(d);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle>Remove User</DialogTitle>
          <DialogDescription>This permanently removes {user?.name} ({user?.email}) and revokes their access. This cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="confirm-delete-user" onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Remove User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SignoutDialog({ user, isSelf, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.signoutUserSessions(user.id, true);
      toast.success(isSelf ? "You have been signed out of all sessions" : `${user.name} signed out of all sessions`);
      onClose();
      if (isSelf) onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (typeof d === "string") toast.error(d);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="signout-dialog">
        <DialogHeader>
          <DialogTitle>Sign out all sessions</DialogTitle>
          <DialogDescription>
            {isSelf
              ? "This will immediately invalidate all of your active sessions, including this one. You will be returned to the login screen."
              : `This will immediately sign ${user?.name} out of every device and invalidate their active sessions.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="confirm-signout" onClick={submit} disabled={busy} className="bg-[#1D4ED8] hover:bg-[#1E40AF]">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Sign out sessions"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
